import { capitalCase } from 'change-case';
import clsx from 'clsx';
import { useState } from 'react';
import { toast } from '~/utils/toast';
import Button from '~/components/Button';
import InputLabel from '~/components/InputLabel';
import Select from '~/components/Select';
import Spinner from '~/components/Spinner';
import Toggle from '~/components/Toggle';
import { Field } from '~/components/ui/field';
import { SmartsheetProjectTypes } from '~/server/enums';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { AgentModelUseCase, RemoteTaskManagerProvider, TaskManagerType } from '@prisma/client';

import { OAuthSteps, type Project, type TaskManagerModalProps } from './TaskManagerModal';
import TextInput from '~/components/TextInput';
import Tooltip from '~/components/Tooltip';
import { TrashIcon, ExclamationTriangleIcon, LinkIcon } from '@heroicons/react/24/outline';
import AllowedEvaluationTypes from '~/app/(secured)/[workspaceId]/projects/[remoteTaskManagerId]/[tab]/_components/TaskManagerSettings/TaskManagersTable/AllowedEvaluationTypes';
import { getProviderLogo } from '~/utils/logo';
import { ConnectIconTile } from './ConnectIconTile';
import { SmartsheetLinkedStep } from './SmartsheetLinkedStep';

type ProjectSelectionProps = Pick<
  TaskManagerModalProps,
  'provider' | 'taskManagerType' | 'closeModal'
> & {
  toggleForProjects: SmartsheetProjectTypes;
  setToggleForSmartsheet: React.Dispatch<React.SetStateAction<SmartsheetProjectTypes>>;
  isProjectListLoading: boolean;
  projects: Project[];
  selectedProject: string;
  setSelectedProject: React.Dispatch<React.SetStateAction<string>>;
  setFieldMapping: React.Dispatch<React.SetStateAction<Record<string, string> | undefined>>;
  setStep: (value: React.SetStateAction<OAuthSteps>) => void;
  setAllPossibleColumns: React.Dispatch<
    React.SetStateAction<
      {
        id: string;
        title: string;
      }[]
    >
  >;
};

export default function ProjectSelection({
  provider,
  toggleForProjects,
  setToggleForSmartsheet,
  isProjectListLoading,
  taskManagerType,
  projects,
  selectedProject,
  setSelectedProject,
  closeModal,
  setAllPossibleColumns,
  setFieldMapping,
  setStep,
}: ProjectSelectionProps) {
  const [allowedEvaluationTypeIds, setAllowedEvaluationTypeIds] = useState<string[]>([]);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [processAttachments, setProcessAttachments] = useState(true);
  const [useCaseFilter, setUseCaseFilter] = useState<AgentModelUseCase>(
    AgentModelUseCase.MARKETING_REVIEW,
  );
  const { isLoading: isLoadingColumnMapping, mutate: getColumnsMapping } =
    api.task.getColumnMapings.useMutation();
  const { data: translationAvailable } = api.config.getTranslationAvailability.useQuery(
    undefined,
    { staleTime: Infinity, refetchOnWindowFocus: false },
  );
  const utils = api.useUtils();
  const { isLoading: isConnectingToProjectLoading, mutate: connectProject } =
    api.task.syncTaskManagerById.useMutation();
  const [isWorkspace, setIsWorkspace] = useState(false);
  const { data: workspacesResponse, isLoading: isWorkspaceListLoading } =
    api.task.getWorkspaces.useQuery(
      { provider: provider! },
      { enabled: isWorkspace && !!provider },
    );
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);
  const { isLoading: isConnectingToWorkspaceLoading, mutate: connectWorkspace } =
    api.task.syncTaskManagersByWorkspaceId.useMutation();

  const isAsana = provider === RemoteTaskManagerProvider.ASANA;

  // Portfolio states
  const { data: portfoliosResponse, isLoading: isPortfolioListLoading } =
    api.task.getPortfolios.useQuery(
      { provider: provider!, remoteWorkspaceId: selectedWorkspace || undefined },
      { enabled: !!selectedWorkspace && isAsana }, // TODO: support other providers
    );
  const [selectedPortfolio, setSelectedPortfolio] = useState<string | null>(null);

  const workspaces = workspacesResponse?.remoteWorkspaces;
  const existingWorkspaces = workspacesResponse?.existingWorkspaces;
  const portfolios = portfoliosResponse?.remotePortfolios;
  const existingPortfolios = portfoliosResponse?.existingPortfolios;
  const isSmartsheet = provider === RemoteTaskManagerProvider.SMARTSHEET;

  const { data: providersStatuses } = api.oAuth.getProviderConnectedStatus.useQuery();
  const { mutate: deleteIntegration, isLoading: isDeletingIntegration } =
    api.oAuth.deleteIntegration.useMutation();
  const { data: allIntegrationMetadata } = api.oAuth.getAllIntegrationMetadata.useQuery();

  const providerStatus = provider ? providersStatuses?.[provider] : undefined;

  const nonNeutralIntegration = allIntegrationMetadata?.find(
    (i) => !i.isInvalid && i.isNeutralAccount === false && i.provider === provider,
  );

  if (isSmartsheet) {
    return (
      <>
        {nonNeutralIntegration ? (
          <div
            key={nonNeutralIntegration.id}
            className="mb-3 rounded-lg border border-blue-200 bg-linear-to-r from-blue-50 to-indigo-50 p-3 shadow-xs"
          >
            <div className="flex items-center justify-between">
              <div className="flex w-full items-center space-x-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-blue-800">
                        <ExclamationTriangleIcon
                          className="mr-1 inline h-4 w-4 shrink-0 text-blue-500"
                          strokeWidth={2}
                        />
                        Recommended: Set up a dedicated bot user for{' '}
                        {capitalCase(nonNeutralIntegration.provider)} integration
                      </div>
                    </div>
                    <Button
                      size="xs"
                      className="ml-2 shrink-0 border-0 bg-blue-600 text-white shadow-xs hover:bg-blue-700"
                      label={
                        <div className="flex items-center space-x-1">
                          <LinkIcon className="h-3 w-3" strokeWidth={2} />
                          <span className="text-xs">Switch</span>
                        </div>
                      }
                      onClick={() => {
                        setStep(OAuthSteps.BEFORE_YOU_PROCEED);
                      }}
                    />
                  </div>
                  <p className="text-xs leading-relaxed text-blue-700">
                    Instead of connecting your personal account, create a dedicated bot user so all
                    automated comments, status changes, and task updates are clearly attributed to a
                    service account rather than appearing as your personal actions. This keeps your
                    activity logs clean, simplifies permission management, and ensures the
                    integration continues working even if team members change roles.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {providerStatus?.tokenEndsWith ? (
          <TextInput
            label="API Token"
            value={`*******${providerStatus.tokenEndsWith}`}
            className="w-full"
            readOnly
          />
        ) : null}

        <SmartsheetLinkedStep
          projectType={toggleForProjects}
          onProjectTypeChange={(type) => {
            setToggleForSmartsheet(type);
            setSelectedProject('');
          }}
          projects={projects
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => ({
              value: project.key,
              label: project.name,
              disabled: project.disabled,
              tooltip: project.disabled ? 'This project has been linked already' : undefined,
            }))}
          selectedProjectId={selectedProject}
          onSelectedProjectChange={setSelectedProject}
          isLoadingProjects={isProjectListLoading}
          isContinuing={isLoadingColumnMapping}
          isDisconnecting={isDeletingIntegration}
          onContinue={() => {
            getColumnsMapping(
              {
                provider,
                remoteProjectId: selectedProject,
                type: toggleForProjects,
              },
              {
                onSuccess: (data) => {
                  if (data) {
                    setFieldMapping(data.mapping);
                    setAllPossibleColumns(data.smartsheetColumns);
                    setStep(OAuthSteps.FIELD_MAPPING);
                  }
                },
                onError: serverError,
              },
            );
          }}
          onCancel={closeModal}
          onDisconnect={
            provider
              ? () =>
                  deleteIntegration(
                    { provider },
                    {
                      onError: serverError,
                      onSuccess: () => {
                        utils.oAuth.getAllIntegrationMetadata.invalidate();
                        closeModal();
                      },
                    },
                  )
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            {provider ? (
              <ConnectIconTile src={getProviderLogo(provider).src} alt="" size="sm" />
            ) : null}
            <h3 className="text-foreground text-lg font-semibold tracking-tight">Connected</h3>
          </div>
          {taskManagerType === TaskManagerType.MARKETING && (
            <div className="flex space-x-4">
              <div className="flex flex-col">
                <label className="label cursor-pointer">
                  <span className={clsx('text-sm mr-2', !isWorkspace && 'font-semibold')}>
                    Projects
                  </span>
                  <input
                    type="radio"
                    className="h-4 w-4 shrink-0 rounded-full border border-primary accent-primary"
                    checked={!isWorkspace}
                    onChange={() => {
                      setIsWorkspace(false);
                      setSelectedWorkspace(null);
                      setSelectedPortfolio(null);
                    }}
                  />
                </label>
              </div>
              <div className="flex flex-col">
                <label className="label cursor-pointer">
                  <span className={clsx('text-sm mr-2', isWorkspace && 'font-semibold')}>
                    Workspaces
                  </span>
                  <input
                    type="radio"
                    className="h-4 w-4 shrink-0 rounded-full border border-primary accent-primary"
                    checked={isWorkspace}
                    onChange={() => {
                      setIsWorkspace(true);
                      setSelectedWorkspace(null);
                      setSelectedPortfolio(null);
                    }}
                  />
                </label>
              </div>
            </div>
          )}
          {provider && (
            <Tooltip content="Disconnect" hideIcon popoverProps={{ disablePortal: true }}>
              <Button
 className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent "
                label={<TrashIcon className="text-error" height={20} />}
                disabled={isDeletingIntegration}
                isLoading={isDeletingIntegration}
                onClick={() =>
                  deleteIntegration(
                    { provider: provider },
                    {
                      onError: serverError,
                      onSuccess: () => {
                        utils.oAuth.getAllIntegrationMetadata.invalidate();
                        closeModal();
                      },
                    },
                  )
                }
              />
            </Tooltip>
          )}
        </div>
        {providerStatus?.tokenEndsWith && (
          <TextInput
            label="API Token"
            value={`*******${providerStatus.tokenEndsWith}`}
            className="w-full"
            readOnly
          />
        )}
      </div>

      {nonNeutralIntegration && (
        <div
          key={nonNeutralIntegration.id}
          className="mb-3 p-3 bg-linear-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-xs"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 w-full">
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-blue-800 text-sm font-semibold">
                      <ExclamationTriangleIcon
                        className="h-4 w-4 text-blue-500 shrink-0 inline mr-1"
                        strokeWidth={2}
                      />
                      Recommended: Set up a dedicated bot user for{' '}
                      {capitalCase(nonNeutralIntegration.provider)} integration
                    </div>
                  </div>
                  <Button size="xs"
                    className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-xs ml-2 shrink-0"
                    label={
                      <div className="flex items-center space-x-1">
                        <LinkIcon className="h-3 w-3" strokeWidth={2} />
                        <span className="text-xs">Switch</span>
                      </div>
                    }
                    onClick={() => {
                      setStep(OAuthSteps.BEFORE_YOU_PROCEED);
                    }}
                  />
                </div>
                <p className="text-blue-700 text-xs leading-relaxed">
                  Instead of connecting your personal account, create a dedicated bot user so all
                  automated comments, status changes, and task updates are clearly attributed to a
                  service account rather than appearing as your personal actions. This keeps your
                  activity logs clean, simplifies permission management, and ensures the integration
                  continues working even if team members change roles.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
      {isWorkspace ? (
        <>
          {isWorkspaceListLoading ? (
            <div className="text-center">
              <Spinner />
            </div>
          ) : (
            <Select
              className="w-full"
              label="Select a Workspace to link with Protege AI"
              options={
                workspaces
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  .map((w) => {
                    const exists = !!existingWorkspaces?.some((i) => i.remoteSpaceId === w.id);
                    return {
                      label: w.name,
                      value: w.id,
                      disabled: exists,
                      tooltip: exists ? 'This workspace has been linked already' : undefined,
                    };
                  }) || []
              }
              value={selectedWorkspace || ''}
              firstOption=""
              onChange={(e) => {
                setSelectedWorkspace(e.target.value || null);
              }}
            />
          )}
          {selectedWorkspace && isAsana && (
            isPortfolioListLoading ? (
              <div className="text-center">
                <Spinner />
              </div>
            ) : (
              <Select
                className="w-full"
                label="Select a Portfolio (Optional)"
                options={
                  portfolios
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    .map((p) => {
                      const exists = !!existingPortfolios?.some((i) => i.name === p.name);
                      return {
                        label: p.name,
                        value: p.id,
                        disabled: exists,
                        tooltip: exists ? 'This portfolio has been linked already' : undefined,
                      };
                    }) || []
                }
                value={selectedPortfolio || ''}
                firstOption="No Portfolio (Link Workspace Only)"
                onChange={(e) => {
                  setSelectedPortfolio(e.target.value || null);
                }}
              />
            )
          )}
        </>
      ) : isProjectListLoading ? (
        <div className="text-center">
          <Spinner />
        </div>
      ) : (
        <Select
          className="w-full"
          label="Select a Project to link with Protege AI"
          options={projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => ({
              label: project.name,
              value: project.key,
              disabled: project.disabled,
              tooltip: project.disabled ? 'This project has been linked already' : undefined,
            }))}
          value={selectedProject}
          firstOption=""
          onChange={(e) => {
            setSelectedProject(e.target.value);
          }}
        />
      )}
      {taskManagerType === TaskManagerType.MARKETING && (
        <>
          <Select<AgentModelUseCase>
            label="Use Case"
            options={[
              {
                label: 'Marketing Review',
                value: AgentModelUseCase.MARKETING_REVIEW,
              },
              {
                label: 'Electronic Communications Review',
                value: AgentModelUseCase.ELECTRONIC_COMMUNICATIONS_REVIEW,
              },
              {
                label: 'Document Q&A',
                value: AgentModelUseCase.DOCUMENT_QA,
              },
            ]}
            value={useCaseFilter}
            onChange={(e) => {
              const value = e.target.value as AgentModelUseCase;
              if (value !== useCaseFilter) {
                setAllowedEvaluationTypeIds([]);
              }
              setUseCaseFilter(value);
            }}
          />
          <AllowedEvaluationTypes
            label={`Select Document Evaluation Type(s) to Use for New Tickets${isWorkspace ? '' : ' (Optional)'}`}
            readOnly={false}
            onUpdate={setAllowedEvaluationTypeIds}
            allowedEvaluationTypeIds={allowedEvaluationTypeIds}
            useCase={useCaseFilter}
          />
          <Field orientation="horizontal" className="min-h-9 justify-between">
            <InputLabel label="Process Attachments" className="pb-0" />
            <Toggle
              checked={processAttachments}
              onCheckedChange={(checked) => {
                setProcessAttachments(checked);
              }}
            />
          </Field>
          {translationAvailable && (
            <Field orientation="horizontal" className="min-h-9 justify-between">
              <InputLabel label="Enable Translation" className="pb-0" />
              <Toggle
                checked={translationEnabled}
                onCheckedChange={(checked) => {
                  setTranslationEnabled(checked);
                }}
              />
            </Field>
          )}
        </>
      )}
      </div>
      <div className="pt-6">
        <Button
          isLoading={isConnectingToProjectLoading || isConnectingToWorkspaceLoading}
          label="Finish"
          disabled={
            (isWorkspace
              ? !selectedWorkspace || allowedEvaluationTypeIds.length === 0
              : !selectedProject) ||
            isConnectingToProjectLoading ||
            isConnectingToWorkspaceLoading
          }
          className="w-full"
          size="sm"
          onClick={() => {
            if (isWorkspace) {
              connectWorkspace(
                {
                  provider: provider!,
                  remoteSpaceId: selectedWorkspace!,
                  allowedEvaluationTypeIds: allowedEvaluationTypeIds,
                  translationEnabled,
                  processAttachments,
                  remotePortfolioId: selectedPortfolio,
                },
                {
                  onError: serverError,
                  onSuccess: () => {
                    closeModal();
                    utils.task.getProjects.invalidate({ type: taskManagerType });
                    utils.task.getWorkspaces.invalidate({ provider: provider! });
                    selectedPortfolio &&
                      utils.task.getPortfolios.invalidate({ provider: provider! });
                    toast.success(
                      `Successfully synced to ${workspaces?.find((p) => p.id === selectedWorkspace)?.name}`,
                    );
                  },
                },
              );
            } else {
              connectProject(
                {
                  provider: provider!,
                  remoteProjectId: selectedProject,
                  allowedEvaluationTypeIds: allowedEvaluationTypeIds,
                  translationEnabled,
                  taskManagerType,
                  processAttachments,
                },
                {
                  onError: serverError,
                  onSuccess: () => {
                    closeModal();
                    utils.task.getProjects.invalidate({ type: taskManagerType });
                    toast.success(
                      `Successfully synced to ${projects.find((p) => p.key === selectedProject)?.name}`,
                    );
                  },
                },
              );
            }
          }}
        />
      </div>
    </>
  );
}
